/* eslint-disable react/no-array-index-key */
/* eslint-disable max-len */
import React from "react"
import { useSelector } from "react-redux"

import { SilResultProjectDevice, SilResultSafetyFunction } from "server/models"
import { StoreType, UserSettings, LangType } from "client/store"
import { sort } from "client/store/utils"

import { parseProps } from "client/pages/sil/formulas"
import { ASC } from "const"

import { FormatScientificString } from "client/pages/sil/format/scientific"
import { FormatProbabilityString } from "client/pages/sil/format/probability"
import { FormatPercentageString } from "client/pages/sil/format/percentage"

import {
    ResultTable,
    ResultTableAnnotation,
    BlockContent,
} from "../styled"


interface ConfigurationProps {
    resultSafetyFunction: SilResultSafetyFunction
}

const Configuration = ({
    resultSafetyFunction,
}: ConfigurationProps) => {
    const userSettings = useSelector((state: StoreType) => state.system.userSettings)
    const lang = useSelector((state: StoreType) => state.system.lang)

    const result = Configurate(resultSafetyFunction, userSettings, lang)

    return (
        <div>
            {
                result.failureRateYesDevices.rows.length > 0 && (
                    <BlockContent>
                        <ResultTable>
                            <thead>
                                <tr>
                                    {
                                        result.failureRateYesDevices.columns.map((column, index) => (
                                            index === 0
                                                ? <th key={column} colSpan={2}>{column}</th>
                                                : <th key={column}>{column}</th>
                                        ))
                                    }
                                </tr>
                            </thead>

                            <tbody>
                                {
                                    result.failureRateYesDevices.rows.map((row, rowIndex) => (
                                        <tr key={rowIndex}>
                                            {
                                                row.columns.map((column, columnIndex) => {
                                                    if (columnIndex === 0) {
                                                        return !row.nested
                                                            ? <td key={columnIndex} colSpan={2}>{column}</td>
                                                            : [
                                                                <td key="indent" width="30px" aria-label="indent" />,
                                                                <td key="value">{column}</td>,
                                                            ]
                                                    }
                                                    return <td key={columnIndex}>{column}</td>
                                                })
                                            }
                                        </tr>
                                    ))
                                }
                            </tbody>
                        </ResultTable>

                        <ResultTableAnnotation>{result.failureRateYesDevices.annotation}</ResultTableAnnotation>
                    </BlockContent>
                )
            }
            {
                result.failureRateNoDevices.rows.length > 0 && (
                    <BlockContent>
                        <ResultTable>
                            <thead>
                                <tr>
                                    {
                                        result.failureRateNoDevices.columns.map((column) => (
                                            <th key={column}>{column}</th>
                                        ))
                                    }
                                </tr>
                            </thead>

                            <tbody>
                                {
                                    result.failureRateNoDevices.rows.map((row, rowIndex) => (
                                        <tr key={rowIndex}>
                                            {
                                                row.map((rowColumn, columnIndex) => (
                                                    <td key={columnIndex}>{rowColumn}</td>
                                                ))
                                            }
                                        </tr>
                                    ))
                                }
                            </tbody>
                        </ResultTable>

                        <ResultTableAnnotation>{result.failureRateNoDevices.annotation}</ResultTableAnnotation>
                    </BlockContent>
                )
            }
        </div>
    )
}
export default Configuration


type RowType = {
    nested: boolean
    columns: string[]
}

export const Configurate = (
    resultSafetyFunction: SilResultSafetyFunction,
    userSettings: UserSettings,
    lang: LangType,
) => {
    // important! get the subsystem list from the function!
    const subsystemList = resultSafetyFunction.subsystems || []

    const uniqueDeviceList: { [deviceId: number]: SilResultProjectDevice } = {}
    subsystemList.forEach((subsystem) => {
        subsystem.blocks.forEach((block) => {
            const { projectDevice } = block
            uniqueDeviceList[projectDevice.id] = projectDevice
        })
    })

    const deviceArrayOrdered: SilResultProjectDevice[] = Object.values(uniqueDeviceList).sort((a, b) => sort(a, b, "name", ASC))
    const failureRateYesDevices: SilResultProjectDevice[] = []
    const failureRateNoDevices: SilResultProjectDevice[] = []

    deviceArrayOrdered.forEach((device) => {
        if (device.failureRate) failureRateYesDevices.push(device)
        else failureRateNoDevices.push(device)
    })


    const columnsYes: string[] = []
    const rowsYes: RowType[] = []

    if (failureRateYesDevices.length > 0) {
        columnsYes.push(
            lang.device,
            lang.model,
            lang.brand,
            lang.type,
            lang.sd,
            lang.su,
            lang.dd,
            lang.du,
        )

        failureRateYesDevices.forEach((device) => {
            const deviceProps = parseProps({
                sd: device.sd,
                su: device.su,
                dd: device.dd,
                du: device.du,
            })

            const rowColumns = [
                device.name,
                device.qidModelName || "",
                device.nested ? lang.subsystem : device.manufacturer,
                device.type,
                deviceProps.sd === null || deviceProps.sd === 0
                    ? lang.n_u
                    : FormatScientificString(deviceProps.sd, userSettings, lang),
                deviceProps.su === null || deviceProps.su === 0
                    ? lang.n_u
                    : FormatScientificString(deviceProps.su, userSettings, lang),
                deviceProps.dd === null || deviceProps.dd === 0
                    ? lang.n_u
                    : FormatScientificString(deviceProps.dd, userSettings, lang),
                deviceProps.du === null || deviceProps.du === 0
                    ? lang.n_u
                    : FormatScientificString(deviceProps.du, userSettings, lang),
            ]

            rowsYes.push({
                nested: false,
                columns: rowColumns,
            })


            // nested device always has "failureRate" = "true"
            device.nestedDevices.forEach((nested) => {
                const nestedProps = parseProps({
                    sd: nested.sd,
                    su: nested.su,
                    dd: nested.dd,
                    du: nested.du,
                })

                const nestedRowColumns = [
                    nested.name,
                    nested.qidModelName || "",
                    nested.manufacturer,
                    nested.type,
                    nestedProps.sd === null || nestedProps.sd === 0
                        ? lang.n_u
                        : FormatScientificString(nestedProps.sd, userSettings, lang),
                    nestedProps.su === null || nestedProps.su === 0
                        ? lang.n_u
                        : FormatScientificString(nestedProps.su, userSettings, lang),
                    nestedProps.dd === null || nestedProps.dd === 0
                        ? lang.n_u
                        : FormatScientificString(nestedProps.dd, userSettings, lang),
                    nestedProps.du === null || nestedProps.du === 0
                        ? lang.n_u
                        : FormatScientificString(nestedProps.du, userSettings, lang),
                ]

                rowsYes.push({
                    nested: true,
                    columns: nestedRowColumns,
                })
            })
        })
    }


    const columnsNo: string[] = []
    const rowsNo: string[][] = []

    if (failureRateNoDevices.length > 0) {
        columnsNo.push(
            lang.device,
            lang.model,
            lang.brand,
            lang.type,
            lang.sff,
            lang.dc,
            lang.pfd,
            lang.pfh,
            lang.sil,
        )

        failureRateNoDevices.forEach((device) => {
            const { pfd, pfh, sff, dc } = parseProps({
                pfd: device.pfd,
                pfh: device.pfh,
                sff: device.sff,
                dc: device.dc,
            })

            const rowColumns = [
                device.name,
                device.qidModelName || "",
                device.manufacturer,
                device.type,
                sff === null || sff === 0
                    ? lang.n_u
                    : FormatPercentageString(sff, userSettings, lang),
                dc === null || dc === 0
                    ? lang.n_u
                    : FormatPercentageString(dc, userSettings, lang),
                pfd === null || pfd === 0
                    ? lang.n_u
                    : FormatProbabilityString(pfd, userSettings, lang),
                pfh === null || pfh === 0
                    ? lang.n_u
                    : FormatProbabilityString(pfh, userSettings, lang),
                device.sil ? lang[`sil_${device.sil}`] : lang.n_u,
            ]

            rowsNo.push(rowColumns)
        })
    }

    return ({
        failureRateYesDevices: {
            columns: columnsYes,
            rows: rowsYes,
            annotation: `${lang.n_u} = ${lang.notUsed}`,
        },

        failureRateNoDevices: {
            columns: columnsNo,
            rows: rowsNo,
            annotation: `${lang.n_u} = ${lang.notUsed}`,
        },
    })
}
